import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { Maximize2, MessageSquareText, Minimize2, Minus, PanelRight } from 'lucide-react'
import { IntuiTooltip } from '@/components/intueri/IntuiTooltip'
import { cn } from '@/lib/utils'
import { useTranslation } from '@/i18n'
import { TroubleshootingWorkflowPanel } from './TroubleshootingPanels'
import type { TroubleshootingWorkflowController } from './useTroubleshootingWorkflow'

/**
 * The floating workflow chat (Figma "3. Create Workflow"), with the BOARD
 * chat's window behavior (BoardChatPanelV2):
 *  - Minus (minimize) hides the card; the board's teal chat FAB (same
 *    markup as BoardCanvas's minimized-chat button) reopens it.
 *  - Maximize2/Minimize2 toggles fullscreen over the canvas area.
 *  - PanelRight toggles fill-height (full canvas height) and back.
 * The transcript stays mounted through every state so the conversation (and a
 * generation in flight) survives.
 *
 * The card is drag-resizable from its top/left edges and top-left corner
 * (it is anchored bottom-right, so those are the free sides). The size
 * persists per browser via localStorage and applies to the default state;
 * fill-height keeps the resized width, fullscreen ignores both.
 */
const CHAT_SIZE_STORAGE_KEY = 'workflow-chat-size'
const CHAT_MIN_WIDTH = 320
const CHAT_MIN_HEIGHT = 360
const CHAT_DEFAULT_SIZE = { width: 420, height: 500 }
// Keep at least this much viewport free so the card can't swallow the canvas.
const CHAT_VIEWPORT_MARGIN = 32

function clampChatSize(size: { width: number; height: number }) {
  return {
    width: Math.min(
      Math.max(size.width, CHAT_MIN_WIDTH),
      Math.max(CHAT_MIN_WIDTH, window.innerWidth - CHAT_VIEWPORT_MARGIN)
    ),
    height: Math.min(
      Math.max(size.height, CHAT_MIN_HEIGHT),
      Math.max(CHAT_MIN_HEIGHT, window.innerHeight - CHAT_VIEWPORT_MARGIN)
    ),
  }
}

function loadChatSize() {
  try {
    const raw = localStorage.getItem(CHAT_SIZE_STORAGE_KEY)
    if (!raw) return CHAT_DEFAULT_SIZE
    const parsed = JSON.parse(raw) as { width?: unknown; height?: unknown }
    if (typeof parsed.width !== 'number' || typeof parsed.height !== 'number') {
      return CHAT_DEFAULT_SIZE
    }
    return clampChatSize({ width: parsed.width, height: parsed.height })
  } catch {
    return CHAT_DEFAULT_SIZE
  }
}

export default function FloatingChatPanel({
  workflow,
  title,
  hidden = false,
  onAttachFile,
  onOpenTemplate,
}: {
  workflow: TroubleshootingWorkflowController
  /** Chat title — the workflow name once known, else "Untitled Chat". */
  title?: string
  /**
   * Hide the chat (card AND reopen FAB) while a docked right panel owns the
   * side of the screen. CSS-hidden, not unmounted: the transcript, an
   * in-flight generation, and the minimize/fullscreen state all survive, so
   * closing the panel restores the chat exactly as it was.
   */
  hidden?: boolean
  onAttachFile?: () => void
  /** Reuse an existing tenant workflow (template-ask "Yes") — see TroubleshootingWorkflowPanel. */
  onOpenTemplate?: (templateId: string) => void
}) {
  const { t } = useTranslation()
  const [minimized, setMinimized] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  const [fillHeight, setFillHeight] = useState(false)
  const [size, setSize] = useState(loadChatSize)
  // Live drag bookkeeping: the anchor is bottom-right, so dragging LEFT/UP
  // grows the card. Window-level listeners so the drag survives leaving the
  // 6px handle; refs so move events don't re-render per pixel needlessly.
  const dragRef = useRef<{
    axis: 'x' | 'y' | 'xy'
    startX: number
    startY: number
    startWidth: number
    startHeight: number
  } | null>(null)

  useEffect(() => {
    const onMove = (event: PointerEvent | MouseEvent) => {
      const drag = dragRef.current
      if (!drag) return
      setSize(
        clampChatSize({
          width:
            drag.axis === 'y' ? drag.startWidth : drag.startWidth + (drag.startX - event.clientX),
          height:
            drag.axis === 'x' ? drag.startHeight : drag.startHeight + (drag.startY - event.clientY),
        })
      )
    }
    const onUp = () => {
      if (!dragRef.current) return
      dragRef.current = null
      setSize((current) => {
        try {
          localStorage.setItem(CHAT_SIZE_STORAGE_KEY, JSON.stringify(current))
        } catch {
          // Private mode / quota — the size just won't persist.
        }
        return current
      })
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [])

  const startResize = (axis: 'x' | 'y' | 'xy') => (event: ReactPointerEvent) => {
    event.preventDefault()
    dragRef.current = {
      axis,
      startX: event.clientX,
      startY: event.clientY,
      startWidth: size.width,
      startHeight: size.height,
    }
  }

  const chatTitle =
    title?.trim() || t('workflow:builder.untitled_chat', { defaultValue: 'Untitled Chat' })

  return (
    <>
      {/* Reopen button — byte-identical to the V2 board's minimized-chat FAB
          (BoardCanvas.tsx: teal circle + MessageSquareText), so the two
          canvases surface chat the same way. Keep the two in sync. */}
      {minimized && !hidden && (
        <button
          type="button"
          onClick={() => setMinimized(false)}
          className="absolute bottom-4 right-4 z-20 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--chat-fab-bg)] text-white shadow-lg transition-colors hover:bg-[var(--chat-fab-bg-hover)]"
          // Deliberately the BOARD's key: one translation for the shared FAB.
          aria-label={t('board:actions.open_chat', { defaultValue: 'Open chat' })}
        >
          <MessageSquareText className="h-5 w-5" />
        </button>
      )}

      <div
        className={cn(
          'pointer-events-auto absolute z-20 flex flex-col overflow-hidden bg-background dark:bg-dark-card',
          (minimized || hidden) && 'hidden',
          fullscreen
            ? 'inset-0 rounded-none border-0'
            : cn(
                'right-4 max-w-[calc(100%-2rem)] rounded-3xl border border-border shadow-[0_4px_24px_rgba(15,23,42,0.10)]',
                fillHeight ? 'bottom-4 top-4' : 'bottom-4 max-h-[calc(100%-2rem)]'
              )
        )}
        style={
          fullscreen
            ? undefined
            : fillHeight
              ? { width: size.width }
              : { width: size.width, height: size.height }
        }
      >
        {/* Drag-resize handles on the free sides (anchored bottom-right).
            Hidden in fullscreen; fill-height keeps only the width handle. */}
        {!fullscreen && (
          <>
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label={t('workflow:builder.resize_chat_width', { defaultValue: 'Resize chat width' })}
              onPointerDown={startResize('x')}
              className="absolute inset-y-0 left-0 z-10 w-1.5 cursor-ew-resize"
            />
            {!fillHeight && (
              <div
                role="separator"
                aria-orientation="horizontal"
                aria-label={t('workflow:builder.resize_chat_height', { defaultValue: 'Resize chat height' })}
                onPointerDown={startResize('y')}
                className="absolute inset-x-0 top-0 z-10 h-1.5 cursor-ns-resize"
              />
            )}
            {!fillHeight && (
              <div
                role="separator"
                aria-label={t('workflow:builder.resize_chat', { defaultValue: 'Resize chat' })}
                onPointerDown={startResize('xy')}
                className="absolute left-0 top-0 z-20 size-3 cursor-nwse-resize"
              />
            )}
          </>
        )}
        {/* Title bar (board chrome): title | new chat · fill height · fullscreen · minimize.
            The title is a plain label — the board's ▾ conversation switcher needs
            multi-conversation support this chat doesn't have yet. */}
        <div className="flex h-12 shrink-0 items-center justify-between px-4">
          <span className="flex min-w-0 items-center text-sm font-medium text-muted-foreground">
            <span className="truncate">{chatTitle}</span>
          </span>
          <div className="flex items-center gap-2">
            {/* No "New Chat" control: the workflow chat is single-conversation,
                so the board's multi-conversation ⊕ doesn't apply here (Pegah,
                workflow feedback 2026-07-14 — "remove new chat if not
                applicable"). Restore it if/when multi-conversation lands. */}
            <IntuiTooltip
              content={
                fillHeight
                  ? t('workflow:builder.restore_height', { defaultValue: 'Restore height' })
                  : t('workflow:builder.fill_height', { defaultValue: 'Fill height' })
              }
            >
              <button
                type="button"
                onClick={() => {
                  setFillHeight((v) => !v)
                  setFullscreen(false)
                }}
                aria-label={
                  fillHeight
                    ? t('workflow:builder.restore_height', { defaultValue: 'Restore height' })
                    : t('workflow:builder.fill_height', { defaultValue: 'Fill height' })
                }
                aria-pressed={fillHeight}
                className="p-0.5 text-muted-foreground transition-colors hover:text-foreground"
              >
                <PanelRight className="size-[18px]" />
              </button>
            </IntuiTooltip>
            <IntuiTooltip
              content={
                fullscreen
                  ? t('workflow:builder.exit_fullscreen', { defaultValue: 'Exit fullscreen' })
                  : t('workflow:builder.fullscreen', { defaultValue: 'Fullscreen' })
              }
            >
              <button
                type="button"
                onClick={() => {
                  // Mirror the fill-height button: the two modes are exclusive,
                  // so exiting fullscreen lands on the DEFAULT height rather
                  // than jumping to a stale fill-height state.
                  setFullscreen((v) => !v)
                  setFillHeight(false)
                }}
                aria-label={
                  fullscreen
                    ? t('workflow:builder.exit_fullscreen', { defaultValue: 'Exit fullscreen' })
                    : t('workflow:builder.fullscreen', { defaultValue: 'Fullscreen' })
                }
                aria-pressed={fullscreen}
                className="p-0.5 text-muted-foreground transition-colors hover:text-foreground"
              >
                {fullscreen ? <Minimize2 className="size-[18px]" /> : <Maximize2 className="size-[18px]" />}
              </button>
            </IntuiTooltip>
            <IntuiTooltip content={t('workflow:builder.minimize_chat', { defaultValue: 'Minimize' })}>
              <button
                type="button"
                onClick={() => setMinimized(true)}
                aria-label={t('workflow:builder.minimize_chat', { defaultValue: 'Minimize' })}
                className="p-0.5 text-muted-foreground transition-colors hover:text-foreground"
              >
                <Minus className="size-[18px]" />
              </button>
            </IntuiTooltip>
          </div>
        </div>

        <div className="min-h-0 flex-1">
          <TroubleshootingWorkflowPanel
            workflow={workflow}
            onAttachFile={onAttachFile}
            onOpenTemplate={onOpenTemplate}
          />
        </div>
      </div>
    </>
  )
}
